import { useInfiniteQuery, useQueries, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';

import { ApiError } from '../api/errors';
import { useAppBar } from '../app/AppBar';
import appStyles from '../app/app.module.css';
import { fetchNoteDetail } from '../review/api';
import { alertMessageLabel } from '../review/findings';
import type { NoteDetail } from '../review/schema';
import { ErrorScreen } from '../shell/ErrorScreen';
import { Icon } from '../ui/Icon';
import { dismissAlert, fetchAlerts, type AlertStatusFilter } from './api';
import { SeverityBadge } from './SeverityBadge';
import styles from './alerts.module.css';
import { groupAlertsByNote, type AlertGroup } from './grouping';
import type { Alert } from './schema';

const PAGE_SIZE = 20;

const FILTERS: readonly AlertStatusFilter[] = ['open', 'resolved'];

const FILTER_LABELS: Readonly<Record<AlertStatusFilter, string>> = {
  open: 'Abiertas',
  resolved: 'Resueltas',
};

/** Extra context beside a message's own text, for the "no blind retry" case (CLAUDE.md rule 8)
 * where the agent's own answer matters as much as the alert itself. */
const AGENT_CONTEXT_BY_KEY: Readonly<Record<string, string>> = {
  'delivery.attention': 'el agente respondió "ambiguo"',
};

type GroupDismissState =
  | { readonly kind: 'idle' }
  | { readonly kind: 'pending' }
  | { readonly kind: 'error'; readonly message: string };

function withoutState(
  states: Record<string, GroupDismissState>,
  key: string,
): Record<string, GroupDismissState> {
  return Object.fromEntries(Object.entries(states).filter(([id]) => id !== key));
}

function groupKey(group: AlertGroup): string {
  return group.noteId ?? '__no_note__';
}

/** Whether at least one alert in the group traces to an unresolved reference field - the
 * distinction between "Resolver las N" (a field resolution, opens the note's resolution flow)
 * and "Ver la nota" (something only checking Excel by hand can fix). */
function isFieldResolvable(alerts: readonly Alert[]): boolean {
  return alerts.some((alert) => alert.reference_issue_id !== null);
}

function noteSummaryLine(detail: NoteDetail | undefined): string {
  if (detail === undefined) {
    return 'Cargando nota…';
  }
  const customer =
    detail.document.customer.official_name ??
    detail.document.customer.recognized_text ??
    'Sin cliente';
  const date = detail.document.note_date ?? 'sin fecha';
  return `${customer} · ${date}`;
}

interface AlertCardProps {
  readonly group: AlertGroup;
  readonly noteDetail: NoteDetail | undefined;
  readonly showActions: boolean;
  readonly dismissState: GroupDismissState;
  readonly onDismiss: () => void;
}

function AlertCard({ group, noteDetail, showActions, dismissState, onDismiss }: AlertCardProps) {
  const resolvable = isFieldResolvable(group.alerts);
  const href =
    group.noteId !== null
      ? resolvable
        ? `/notas/${group.noteId}?resolver=1`
        : `/notas/${group.noteId}`
      : null;

  return (
    <li className={styles.card}>
      <div className={styles.cardTop}>
        {noteDetail?.image !== null && noteDetail?.image !== undefined && group.noteId !== null ? (
          <img
            className={styles.cardThumb}
            src={`/notes/${group.noteId}/image`}
            alt="Foto de la nota"
            loading="lazy"
          />
        ) : null}
        <span className={styles.cardTitle}>{noteSummaryLine(noteDetail)}</span>
      </div>

      <ul className={styles.alertList}>
        {group.alerts.map((alert) => {
          const context = AGENT_CONTEXT_BY_KEY[alert.message_key];
          return (
            <li key={alert.id} className={styles.alertRow}>
              {/* The severity badge carries its own icon, tone, and label - the design's
                  vocabulary for this, and never colour alone (`ui-requirements.md` §10.4). */}
              <SeverityBadge severity={alert.severity} />
              <span className={styles.alertText}>
                {alertMessageLabel(alert.message_key, {
                  kind: alert.reference_kind,
                  observedText: alert.reference_observed_text,
                })}
                {context !== undefined ? ` (${context})` : ''}
                <span className={styles.alertTimestamp}> · {alert.created_at.slice(0, 10)}</span>
              </span>
            </li>
          );
        })}
      </ul>

      {showActions ? (
        <>
          <div className={styles.cardActions}>
            {href !== null ? (
              <Link className={styles.primaryAction} to={href}>
                {resolvable
                  ? group.alerts.length === 1
                    ? 'Resolver'
                    : `Resolver las ${String(group.alerts.length)}`
                  : 'Ver la nota'}
              </Link>
            ) : null}
            <button
              type="button"
              className={styles.secondaryAction}
              disabled={dismissState.kind === 'pending'}
              onClick={onDismiss}
            >
              {dismissState.kind === 'pending'
                ? 'Descartando…'
                : resolvable
                  ? 'Descartar'
                  : 'Ya lo comprobé'}
            </button>
          </div>
          {dismissState.kind === 'error' ? (
            <p className={styles.dismissError} role="alert">
              {dismissState.message}
            </p>
          ) : null}
        </>
      ) : (
        group.alerts.map((alert) =>
          alert.resolved_at !== null ? (
            <p key={alert.id} className={styles.resolvedNote}>
              Resuelta el {alert.resolved_at.slice(0, 10)}
            </p>
          ) : null,
        )
      )}
    </li>
  );
}

/**
 * The alert inbox, grouped by the note each alert points at (redesign: "group alerts by note").
 * Purely system-level signals (stale Excel references) no longer render here at all - that
 * migrated to the Notas screen's "Sistema" sheet, so this list only ever shows note-scoped
 * alerts once the backend stops emitting note-less system alerts through `GET /alerts` too
 * (see this task's reported gap: today a note-less alert would still group under "sin nota").
 */
export function AlertsScreen() {
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState<AlertStatusFilter>('open');
  const [dismissStates, setDismissStates] = useState<Record<string, GroupDismissState>>({});

  const alertsQuery = useInfiniteQuery({
    queryKey: ['alerts', 'list', filter],
    queryFn: ({ pageParam }: { pageParam: string | undefined }) =>
      fetchAlerts({ status: filter, cursor: pageParam, limit: PAGE_SIZE }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
  });

  const rows = alertsQuery.data?.pages.flatMap((page) => page.items) ?? [];
  const groups = groupAlertsByNote(rows);
  const openCount = alertsQuery.data?.pages[0]?.open_count;
  const noteCount = new Set(
    rows.map((row) => row.note_id).filter((id): id is string => id !== null),
  ).size;

  const noteIds = [
    ...new Set(groups.map((group) => group.noteId).filter((id): id is string => id !== null)),
  ];
  const noteDetailQueries = useQueries({
    queries: noteIds.map((id) => ({
      queryKey: ['note', id],
      queryFn: () => fetchNoteDetail(id),
    })),
  });
  const noteDetailById = new Map(noteIds.map((id, index) => [id, noteDetailQueries[index]?.data]));

  // The design carries the count inline in the title and the Abiertas/Resueltas toggle inside
  // the bar's fill rather than as the first row of the content column.
  useAppBar(
    () => ({
      title: (
        <>
          Alertas{' '}
          {openCount === undefined ? null : (
            <span className={appStyles.headerTitleCount}>
              ({String(openCount)} {openCount === 1 ? 'abierta' : 'abiertas'})
            </span>
          )}
        </>
      ),
      subtitle:
        noteCount === 0
          ? undefined
          : `En ${String(noteCount)} ${noteCount === 1 ? 'nota' : 'notas'}`,
      below: (
        <div className={appStyles.headerToggle} role="group" aria-label="Filtrar por estado">
          {FILTERS.map((value) => (
            <button
              key={value}
              type="button"
              className={[
                appStyles.headerToggleButton,
                value === filter ? appStyles.headerToggleButtonActive : null,
              ]
                .filter(Boolean)
                .join(' ')}
              aria-pressed={value === filter}
              onClick={() => {
                setFilter(value);
              }}
            >
              {FILTER_LABELS[value]}
            </button>
          ))}
        </div>
      ),
    }),
    [openCount, noteCount, filter, setFilter],
  );

  async function handleDismissGroup(group: AlertGroup): Promise<void> {
    const key = groupKey(group);
    setDismissStates((previous) => ({ ...previous, [key]: { kind: 'pending' } }));
    try {
      for (const alert of group.alerts) {
        if (alert.resolved_at === null) {
          await dismissAlert(alert.id);
        }
      }
      await queryClient.invalidateQueries({ queryKey: ['alerts'] });
      setDismissStates((previous) => withoutState(previous, key));
    } catch (caught) {
      if (!(caught instanceof ApiError)) {
        throw caught;
      }
      setDismissStates((previous) => ({
        ...previous,
        [key]: { kind: 'error', message: caught.message },
      }));
    }
  }

  return (
    <div className={styles.screen}>
      {alertsQuery.isPending ? (
        <p className={styles.inlineLoading} role="status" aria-live="polite">
          Cargando alertas…
        </p>
      ) : alertsQuery.isError && rows.length === 0 ? (
        <ErrorScreen
          message="No se pudieron cargar las alertas. Revisa tu conexión e intenta de nuevo."
          detail={alertsQuery.error instanceof Error ? alertsQuery.error.message : undefined}
          onRetry={() => {
            void alertsQuery.refetch();
          }}
        />
      ) : groups.length === 0 ? (
        <div className={styles.emptyState}>
          <Icon name="check-circle" size={28} strokeWidth={1.8} className={styles.emptyStateIcon} />
          <p className={styles.emptyStateTitle}>Nada pendiente</p>
          <p>
            {filter === 'open'
              ? 'No tienes alertas abiertas en este momento.'
              : 'Todavía no has descartado ninguna alerta.'}
          </p>
        </div>
      ) : (
        <>
          <ul className={styles.groupList}>
            {groups.map((group) => (
              <AlertCard
                key={groupKey(group)}
                group={group}
                noteDetail={group.noteId !== null ? noteDetailById.get(group.noteId) : undefined}
                showActions={filter === 'open'}
                dismissState={dismissStates[groupKey(group)] ?? { kind: 'idle' }}
                onDismiss={() => {
                  void handleDismissGroup(group);
                }}
              />
            ))}
          </ul>
          {alertsQuery.hasNextPage ? (
            <button
              type="button"
              className={styles.loadMoreButton}
              disabled={alertsQuery.isFetchingNextPage}
              onClick={() => {
                void alertsQuery.fetchNextPage();
              }}
            >
              {alertsQuery.isFetchingNextPage ? 'Cargando…' : 'Cargar más'}
            </button>
          ) : null}
        </>
      )}
    </div>
  );
}
